import type { LogsInitConfiguration, StatusType } from '@datadog/browser-logs'

import serverConfig from '../../server/config'
import modulesApi from './modules/api'
import { parsePolicyKey } from './modules/policyKey'

const getConfig = () => {
  const api = modulesApi

  const appName = 'accounts-portal'
  const platform = '3MP'
  const env = process.env.APP_ENV
  if (!env) {
    throw new Error('APP_ENV was not set')
  }
  const supplyURLBase = process.env.SUPPLY_URL_BASE

  const launchDarklyClientSideId = process.env.LAUNCHDARKLY_SDK_CLIENT_SIDE_ID
  if (!launchDarklyClientSideId) {
    throw new Error('LAUNCHDARKLY_SDK_CLIENT_SIDE_ID was not set')
  }

  const stripeKey = process.env.STRIPE_PUBLIC_KEY
  if (!stripeKey) {
    throw new Error('STRIPE_PUBLIC_KEY was not set')
  }
  const stripe = {
    pKey: stripeKey,
  } as const

  const segmentKey = process.env.REACT_APP_SEGMENT_WRITE_KEY
  if (!segmentKey) {
    throw new Error('REACT_APP_SEGMENT_WRITE_KEY was not set')
  }
  const segment = {
    wKey: segmentKey,
    bypassConsent:
      process.env.SEGMENT_CONSENT_BYPASS &&
      process.env.SEGMENT_CONSENT_BYPASS !== 'false',
  }

  const brazeApiKey = process.env.PUBLIC_BRAZE_API_KEY
  if (!brazeApiKey) {
    throw new Error('PUBLIC_BRAZE_API_KEY was not set')
  }
  const brazeApiEndpoint = process.env.PUBLIC_BRAZE_API_ENDPOINT
  if (!brazeApiEndpoint) {
    throw new Error('PUBLIC_BRAZE_API_ENDPOINT was not set')
  }
  const braze = {
    apiKey: brazeApiKey,
    apiEndpoint: brazeApiEndpoint,
    allowUserSuppliedJavascript:
      process.env.PUBLIC_BRAZE_ALLOW_USER_SUPPLIED_JAVASCRIPT === 'true',
  } as const

  const datadogToken = process.env.DATADOG_CLIENT_TOKEN
  if (!datadogToken) {
    throw new Error('DATADOG_CLIENT_TOKEN was not set')
  }
  const datadog: LogsInitConfiguration & {
    level: StatusType
    enabled: boolean
  } = {
    clientToken: datadogToken,
    version: process.env.BUILD_ID || 'unspecified',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    sessionSampleRate: 100,
    level: process.env.LOG_LEVEL as StatusType,
    enabled: !!process.env.DATADOG_LOGS && process.env.DATADOG_LOGS !== 'false',
  }

  const gtmId = process.env.GTM_ID
  if (!gtmId) {
    throw new Error('GTM_ID was not set')
  }
  const googleTagManager = {
    trackingId: gtmId,
  } as const

  // Balance SDK URL - defaults to sandbox for development
  const balanceSdkUrl =
    process.env.BALANCE_SDK_URL ||
    'https://checkout-v2.sandbox.getbalance.com/sdk-latest.js'

  const pusher = {
    appKey: process.env.PUSHER_APP_KEY ?? null,
    cluster: process.env.PUSHER_APP_CLUSTER ?? null,
  }

  // product wants 256 but FusionAuth fails at over 75
  const emailMax = 75

  // cooldown period (in seconds) before another verification code can be requested
  // should match the BE value; 0 = (disabled, no cooldown), > 0 = (cooldown active)
  const verificationCooldownSeconds = parseInt(
    process.env.VERIFICATION_CODE_COOLDOWN_SECONDS || '0',
    10
  )

  // the current versioned terms policy key (see ./modules/policyKey.ts);
  // deliberately required with no empty-means-off behavior
  const loginTermsPolicyKey = parsePolicyKey(process.env.LOGIN_TERMS_POLICY_KEY)
  if (!loginTermsPolicyKey) {
    throw new Error(
      'LOGIN_TERMS_POLICY_KEY was not set or is not a valid policy key'
    )
  }

  return {
    ...serverConfig,
    api,
    appName,
    platform,
    env,
    supplyURLBase,
    launchDarklyClientSideId,
    stripe,
    segment,
    braze,
    datadog,
    googleTagManager,
    emailMax,
    balanceSdkUrl,
    verificationCooldownSeconds,
    loginTermsPolicyKey,
    serviceHosts: api.serviceHosts,
    pusher,
  }
}

type Config = ReturnType<typeof getConfig> & {
  userCountryCode?: string
}

declare global {
  interface Window {
    FORWARD_CONFIG?: Config
  }
}

const config: Config =
  typeof window !== 'undefined' && window.FORWARD_CONFIG
    ? window.FORWARD_CONFIG
    : getConfig()

export default config

export const getHost = (key: 'ACCOUNTS' | 'FUSIONAUTH') => {
  if (key === 'ACCOUNTS') return config.api.serviceHosts.accounts
  if (key === 'FUSIONAUTH') return config.api.serviceHosts.fusionauth
}
